import React, { useCallback, useMemo } from 'react'
import { IconTile, Spinner } from '@mytours/components'
import { AudioPreview, AudioSectionContainer, Screen } from 'mytours-read-common'

import { formatPlayPauseLabel } from '../../functions'
import { useStopAudio } from '../../useStopAudio'
import strings from '../../../../strings'

import './styles/style.less'

interface Props {
	audioPreview?: AudioPreview
	container?: AudioSectionContainer
	collectionId?: number
	screen?: Screen
	// What the control is speaking for, used to name it: "Play audio track <name>". The stop row passes
	// the row's own name ("Stop 1: Welcome"), which is more use to a screen reader user swiping a long
	// list than the audio file's title would be.
	name: string
	className?: string
}

/**
 * The smallest of the audio players: one control, play and pause, and nothing else.
 *
 * `AudioPlayerBar` is the mini bar with a seek bar and skip buttons, `FullscreenAudioPlayer` is the full
 * transport with artwork and transcript, `AudioPlayerTransport` is the inline one an expanded stop row
 * reveals, and this is the pill that sits on a thumbnail. All of them drive the same Howler singleton, so
 * pressing this and then reaching for any of the others controls one sound, not two.
 *
 * This and the transport are the same playback seen twice — `useStopAudio` is what makes that true rather
 * than a claim: press play here, expand the row, and the transport picks up mid-track because neither
 * component holds any state of its own.
 *
 * It draws an `IconTile` and owns nothing about how that looks. It owns its accessible name, which flips
 * between "Play …" and "Pause …" — that name is the reason this is a control here rather than a slot the
 * library names: only a component watching the store knows which verb is true.
 */
const AudioPlayerSquare: React.FC<Props> = ({ audioPreview, container, collectionId, screen, name, className }) => {
	const track = useMemo(
		() => ({ audioPreview, container, collectionId, screen }),
		[audioPreview, container, collectionId, screen],
	)
	const { playable, isPlaying, isLoading, playPause } = useStopAudio(track)

	// Starting a track may mean more than starting it — whatever this pill sits on is free to act on the
	// press as it bubbles, and the stop row opens on it, which is right: the panel is where the full
	// transport is. Stopping one means only stopping it. Letting a pause bubble would reopen the row the
	// reader collapsed to reach this control, so the press stops here when the glyph says pause.
	const onClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			if (isPlaying) event.stopPropagation()
			void playPause()
		},
		[isPlaying, playPause],
	)

	if (!playable) {
		return null
	}

	return (
		<button
			type="button"
			className={`audio-player-square${className ? ` ${className}` : ''}`}
			aria-label={formatPlayPauseLabel(isPlaying, name)}
			// `aria-label` is read once by some screen readers unless the node changes, and this one flips
			// with playback state. Same remount trick the other players use for their play/pause button.
			key={formatPlayPauseLabel(isPlaying, name)}
			onClick={onClick}
		>
			{/* A pill over the picture, not a wash across it. `size` is the pill's height and the inline
			    floor; the two glyphs set its width. The tap target is the button, which stretches to the
			    whole thumbnail — 96x64 in a stop row — so shrinking the tile costs nothing to hit.

			    It was `shape="square" size="100%"` and covered the thumbnail. `glass` carries a backdrop
			    blur, so at that size it blurred the entire photograph underneath: a list of stops came out
			    with the audio ones as smears between the sharp ones. The scrim is unchanged — it is the
			    area it covers that was wrong. */}
			<IconTile variant="glass" shape="circle" size="1.75rem">
				<i className="fal fa-headphones audio-player-square-headphones" aria-hidden="true" />
				{isLoading ? (
					<Spinner variant="crescent" className="audio-player-square-spinner" aria-label={strings.loading} />
				) : (
					<i className={`fas fa-${isPlaying ? 'pause' : 'play'} audio-player-square-glyph`} aria-hidden="true" />
				)}
			</IconTile>
		</button>
	)
}

export default AudioPlayerSquare
